import { toast } from "sonner";
import { api, formatApiDetail } from "../lib/api";
import { loadBluMessages, saveBluMessages } from "./bluChatStorage";
import {
  BLU_CHAT_UPDATED_EVENT,
  clearBluPending,
  dispatchBluChatUpdated,
  getBluPending,
  setBluPending,
} from "./bluChatPending";
import { parseBluFinalize, resolveBluDocPayload } from "./parseBluFinalize";
import { parseBluNoteFinalize } from "./parseBluNoteFinalize";
import { wantsLiveNotionFetch, wantsBluScopingContext, guessClientIdFromMessage, extractPortalNoteTitleHint } from "./bluNotionIntent";

let activeListeners = 0;
let inFlightPromise = null;

export function registerBluChatListener() {
  activeListeners += 1;
  return () => {
    activeListeners = Math.max(0, activeListeners - 1);
  };
}

export function isBluChatRunning() {
  // localStorage "pending" without an in-flight promise in this tab means a crashed
  // refresh or corrupt row — do not treat as running (see reconcileOrphanBluPending).
  return Boolean(inFlightPromise);
}

/** Clear persisted pending when no JS turn is active (orphan after reload / crash). */
export function reconcileOrphanBluPending() {
  if (inFlightPromise) return;
  if (!getBluPending()) return;
  clearBluPending();
  dispatchBluChatUpdated();
}

function formatPortalNoteForBlu(note) {
  if (!note) return "";
  const title = note.title ? String(note.title).trim() : "Untitled";
  const updated = note.updated_at ? String(note.updated_at).trim() : "";
  const content = note.content ? String(note.content).trim() : "";
  const head = updated ? `Title: ${title}\nUpdated: ${updated}` : `Title: ${title}`;
  return content ? `${head}\n\n${content}` : head;
}

export function runBluChatTurn({
  text,
  messages,
  clients,
  systemPrompt,
  onPhase,
  skipUserAppend = false,
  preferredClientId = null,
}) {
  if (inFlightPromise) return inFlightPromise;

  const raw = text.trim();
  let updated = messages;
  if (!skipUserAppend) {
    const userMsg = { role: "user", content: raw, sentAt: new Date().toISOString() };
    updated = [...messages, userMsg];
    saveBluMessages(updated);
    dispatchBluChatUpdated();
  }

  const tryContext = wantsBluScopingContext(raw);
  const clientId = tryContext
    ? guessClientIdFromMessage(raw, clients) || preferredClientId || null
    : null;
  const noteTitleHint = tryContext ? extractPortalNoteTitleHint(raw) : null;
  const tryNotion = wantsLiveNotionFetch(raw) && clientId;
  const tryPortalNotes = tryContext && clientId;
  const tryProspectContext = tryContext && clientId;
  const initialPhase =
    (tryNotion || tryPortalNotes || tryProspectContext) && clientId ? "notion" : "thinking";
  setBluPending({ phase: initialPhase });
  onPhase?.(initialPhase);

  inFlightPromise = (async () => {
    let notionNotes = "";
    let portalNotes = "";
    let prospectContext = "";
    try {
      if ((tryNotion || tryPortalNotes || tryProspectContext) && clientId) {
        setBluPending({ phase: "notion" });
        onPhase?.("notion");
      }

      if (tryPortalNotes && clientId) {
        try {
          const params = { client_id: clientId };
          if (noteTitleHint) params.title_contains = noteTitleHint;
          const { data } = await api.get("/admin/blu/portal-notes", { params });
          const notes = Array.isArray(data) ? data : [];
          if (notes.length) {
            portalNotes = formatPortalNoteForBlu(notes[0]);
          } else if (noteTitleHint) {
            toast.message(`No portal note matched "${noteTitleHint}" — try checking the title in Notes.`);
          }
        } catch (e) {
          toast.error(formatApiDetail(e.response?.data?.detail, "Could not load portal Overview notes"));
        }
      }

      if (tryProspectContext && clientId) {
        try {
          const { data } = await api.get("/admin/blu/prospect-context", {
            params: { client_id: clientId },
          });
          prospectContext = (data?.text || "").trim();
        } catch (e) {
          toast.error(formatApiDetail(e.response?.data?.detail, "Could not load prospect meeting notes"));
        }
      }

      if (tryNotion && clientId) {
        try {
          const { data } = await api.post("/admin/blu/fetch-notes", { client_id: clientId });
          notionNotes = data.text || "";
          (data.warnings || []).forEach((w) => {
            if (w) toast.message(String(w));
          });
        } catch (e) {
          toast.error(formatApiDetail(e.response?.data?.detail, "Could not load Notion notes"));
        }
      } else if (tryNotion && !clientId) {
        toast.message("Name the client clearly so I can load Notion (e.g. “Studio 7 Miami”).");
      } else if (tryContext && !clientId) {
        toast.message("Name the client or prospect clearly so I can load their notes (e.g. “Aurora Studio”).");
      }

      setBluPending({ phase: "thinking" });
      onPhase?.("thinking");

      const payload = {
        messages: updated.map((m) => ({ role: m.role, content: m.content })),
        system_prompt: systemPrompt,
      };
      if (notionNotes.trim()) payload.notion_notes = notionNotes;
      if (portalNotes.trim()) payload.portal_notes = portalNotes;
      if (prospectContext.trim()) payload.prospect_context = prospectContext;

      const { data } = await api.post("/admin/blu/chat", payload);
      const assistantContent = data.text || "—";
      const assistantMsg = {
        role: "assistant",
        content: assistantContent,
        sentAt: new Date().toISOString(),
      };

      const stored = loadBluMessages() || updated;
      const base = stored.length >= updated.length ? stored : updated;
      saveBluMessages([...base, assistantMsg]);
      dispatchBluChatUpdated();

      const doc = parseBluFinalize(assistantContent, { clients }) || resolveBluDocPayload(assistantContent, {
        clients,
        messages: [...base, assistantMsg],
        messageIndex: base.length,
      });
      const note = parseBluNoteFinalize(assistantContent);
      const away = activeListeners === 0 || document.hidden;
      if (doc) {
        toast.success(`BLU built a scope + invoice for ${doc.client_name}`);
      } else if (note) {
        toast.success(`BLU drafted a meeting summary for ${note.client_name}`);
      } else if (away) {
        toast.success("BLU replied — open chat to read");
      }

      return { assistantMsg, doc };
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "BLU is unavailable"));
      const errMsg = {
        role: "assistant",
        content: "Connection issue — check integrations + API key.",
        sentAt: new Date().toISOString(),
      };
      const stored = loadBluMessages() || updated;
      saveBluMessages([...stored, errMsg]);
      dispatchBluChatUpdated();
      if (activeListeners === 0 || document.hidden) {
        toast.message("BLU could not finish — open chat to retry");
      }
      throw e;
    } finally {
      clearBluPending();
      onPhase?.(null);
      inFlightPromise = null;
    }
  })();

  return inFlightPromise;
}

export function subscribeBluChatUpdated(handler) {
  if (typeof window === "undefined") return () => {};
  const fn = () => handler();
  window.addEventListener(BLU_CHAT_UPDATED_EVENT, fn);
  return () => window.removeEventListener(BLU_CHAT_UPDATED_EVENT, fn);
}
